import React from "react";
import { unwrapResult } from "@reduxjs/toolkit";
import { urlHelper } from "components/RouteController/functions/urlHelper";
import { useNavigate } from "components/RouteController/functions/useNavigate";
import { ApplicationStatus } from "models/ApplicationStatus.model";
import { usePostHog } from "posthog-js/react";
import { ServiceError, ServiceErrorCode } from "services/ServiceError";
import { fetchAccessRequests, requestApplicationAccess } from "store/domain-data/access-request/accessRequest";
import { useStoreDispatch } from "store/hooks";

export const useAccessRequestActions = () => {
  const dispatch = useStoreDispatch();
  const { navigateTo } = useNavigate();
  const posthog = usePostHog();

  const openApplication = React.useCallback(
    async (applicationId: number, applicationStatus: ApplicationStatus) => {
      let getUrl = urlHelper.getUrlForApplicationDetailsPage;
      if (applicationStatus === ApplicationStatus.Draft) {
        getUrl = urlHelper.getUrlForApplicationFormPage;
      }

      navigateTo(getUrl({ applicationId }));
    },
    [navigateTo]
  );

  const requestAccess = React.useCallback(
    async (applicationId: number) => {
      try {
        await dispatch(requestApplicationAccess(applicationId)).then(unwrapResult);
        await dispatch(fetchAccessRequests());
        posthog?.capture(`user access-requested`, { success: true });
      } catch (e) {
        if (
          ServiceError.isServiceError(e) &&
          [
            ServiceErrorCode.AccessRequestAlreadyGranted,
            ServiceErrorCode.AccessRequestExist,
            ServiceErrorCode.AccessRequestApplicationNotEditable,
          ].includes(e.code)
        ) {
          posthog?.capture(`user access-requested`, {
            success: false,
            errorCode: e.code,
            errorMessage: e.message,
          });
        }

        throw e;
      }
    },
    [posthog, dispatch]
  );

  return {
    openApplication,
    requestAccess,
  };
};
